import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { NotificationEvent } from '../../../../../api/notifications/notification.types';
import {
  useNotificationCollection,
  getNotificationDisplay,
  formatNotificationTimestamp,
} from './notification-collection.hooks';
import {
  NotificationOverlay,
  NotificationCenterContent,
  PanelHeader,
  PanelTitle,
  MarkAllReadButton,
  NotificationsList,
  NotificationRow,
  NotificationIconWrapper,
  UnreadDot,
  NotificationTextWrapper,
  NotificationTitle,
  NotificationTimestamp,
  NotificationDescription,
  EmptyStateText,
  NotificationDivider,
  LoadMoreSpinner,
  FileDownloadLink,
  AudioSeekerWrapper,
  SwipeableRowWrapper,
  SwipeBg,
  SwipeRowContainer,
} from './notification-collection.styled';
import { UniversalSpinner } from '../../../../../components/universal-spinner/universal-spinner';
import { ConversationListenButton } from '../../../conversation/listen-button/listen-button';
import { useNotificationAudio } from '../../../../../hooks/use-notification-audio';

const SWIPE_THRESHOLD = 80;

const SwipeableRow: React.FC<{
  children: React.ReactNode;
  onDismiss: () => void;
}> = ({ children, onDismiss }) => {
  const [dx, setDx] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const swipeRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    locked: boolean; // true once we know direction is horizontal
    pointerId: number | null;
  }>({ active: false, startX: 0, startY: 0, locked: false, pointerId: null });

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    swipeRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      locked: false,
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const s = swipeRef.current;
    if (!s.active) return;

    const moveX = e.clientX - s.startX;
    const moveY = e.clientY - s.startY;

    if (!s.locked) {
      // Need enough movement to determine direction
      if (Math.abs(moveX) < 8 && Math.abs(moveY) < 8) return;
      if (Math.abs(moveY) > Math.abs(moveX)) {
        // Vertical — abort swipe, let scroll happen
        s.active = false;
        setDx(0);
        return;
      }
      s.locked = true;
    }

    setDx(moveX);
  }, []);

  const onPointerUp = useCallback(() => {
    const s = swipeRef.current;
    if (!s.active && !s.locked) {
      setDx(0);
      return;
    }
    s.active = false;
    s.locked = false;

    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      setDismissing(true);
      import('../../../../../platform/haptics').then(h => h.hapticLight());
      setTimeout(onDismiss, 200);
    } else {
      setDx(0);
    }
  }, [dx, onDismiss]);

  const onPointerCancel = useCallback(() => {
    swipeRef.current.active = false;
    swipeRef.current.locked = false;
    setDx(0);
  }, []);

  return (
    <SwipeRowContainer>
      {dx !== 0 && (
        <SwipeBg style={{ justifyContent: dx > 0 ? 'flex-start' : 'flex-end' }}>
          Mark as read
        </SwipeBg>
      )}
      <SwipeableRowWrapper
        $dx={dx}
        $dismissing={dismissing}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {children}
      </SwipeableRowWrapper>
    </SwipeRowContainer>
  );
};

interface NotificationCollectionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: NotificationEvent[];
  onMarkRead?: (eventId: string) => Promise<void>;
  onMarkAllRead?: () => Promise<void>;
  onDismiss?: (eventId: string) => Promise<void>;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  onOpenConversation?: ((messageId?: string) => void) | undefined;
  /** Called when the listen pill is tapped on a message_received notification — opens conversation + auto-plays */
  onListenInConversation?: ((messageId: string) => void) | undefined;
}

const NotificationCollection: React.FC<NotificationCollectionProps> = ({
  open,
  onOpenChange,
  notifications,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  onOpenConversation,
  onListenInConversation,
}) => {
  const { hasNotifications, handleNotificationClick, handleScroll, listRef, observeRow, visuallyUnreadIds } =
    useNotificationCollection({
      notifications,
      onMarkRead,
      onLoadMore,
      hasMore,
      isLoading,
      onOpenConversation,
      open,
    });

  const audio = useNotificationAudio();

  // Stop audio when panel closes (only if not actively playing)
  React.useEffect(() => {
    if (!open && !audio.playing && !audio.paused) {
      audio.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const handleOverlayClick = () => {
    // Don't close panel if audio is actively playing or paused
    if (audio.playing || audio.paused || audio.loading) return;
    onOpenChange(false);
  };

  const handleContentClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return createPortal(
    <>
      <NotificationOverlay onClick={handleOverlayClick} />
      <NotificationCenterContent onClick={handleContentClick}>
        <PanelHeader>
          <PanelTitle>Notifications</PanelTitle>
          {unreadCount > 0 && onMarkAllRead && (
            <MarkAllReadButton onClick={onMarkAllRead}>Mark all read</MarkAllReadButton>
          )}
        </PanelHeader>

        {hasNotifications ? (
          <NotificationsList ref={listRef} onScroll={handleScroll}>
            {notifications.map((notification, index) => {
              const display = getNotificationDisplay(notification);
              const isUnread = !notification.is_read;
              const isVisuallyUnread = visuallyUnreadIds.has(notification.id);
              const isActiveAudio = audio.activeId === notification.id;

              const rowContent = (
                <>
                  <NotificationRow
                    ref={(node: HTMLDivElement | null) => observeRow(node, notification.id, isUnread)}
                    isFirst={index === 0}
                    isUnread={isVisuallyUnread}
                    onClick={() => {
                      // Don't close panel if audio is actively playing/paused for this notification
                      const isAudioActive = isActiveAudio &&
                        (audio.playing || audio.paused || audio.loading);
                      if (isAudioActive) return;

                      handleNotificationClick(notification);
                      onOpenChange(false);
                    }}
                  >
                    <NotificationIconWrapper isUnread={isVisuallyUnread}>
                      <img
                        src="/logo-no-dot.svg"
                        alt="Notification"
                        width={14}
                        height={14}
                        style={{ display: 'block' }}
                      />
                    </NotificationIconWrapper>

                    <NotificationTextWrapper>
                      <NotificationTitle>{display.title}</NotificationTitle>
                      <NotificationTimestamp>
                        {formatNotificationTimestamp(notification.created_at)}
                      </NotificationTimestamp>
                      <NotificationDescription>{display.description}</NotificationDescription>
                      {notification.event_type === 'custom' && notification.file_url && (
                        <FileDownloadLink
                          href={notification.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={notification.file_name || undefined}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {notification.file_name || 'Download file'}
                        </FileDownloadLink>
                      )}
                      {(() => {
                        const audioUrl = notification.audio_url || notification.payload?.audio_url;
                        if (!audioUrl) return null;

                        const messageId = notification.payload?.message_id;
                        const canListenInConversation =
                          notification.event_type === 'message_received' &&
                          !!messageId &&
                          !!onListenInConversation;

                        return (
                          <AudioSeekerWrapper
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <ConversationListenButton
                              hasAudio
                              loading={!canListenInConversation && audio.loading && isActiveAudio}
                              playing={!canListenInConversation && audio.playing && isActiveAudio}
                              paused={!canListenInConversation && audio.paused && isActiveAudio}
                              durationMs={!canListenInConversation && isActiveAudio ? audio.durationMs : 0}
                              progressPct={!canListenInConversation && isActiveAudio ? audio.progressPct : 0}
                              elapsedMs={!canListenInConversation && isActiveAudio ? audio.elapsedMs : 0}
                              onStart={() => {
                                if (canListenInConversation) {
                                  if (!notification.is_read) onMarkRead?.(notification.id);
                                  onListenInConversation(messageId);
                                  onOpenChange(false);
                                } else {
                                  audio.start(notification.id, audioUrl);
                                }
                              }}
                              onTogglePause={canListenInConversation ? () => {} : audio.togglePause}
                              onSeek={canListenInConversation ? () => {} : audio.seek}
                            />
                          </AudioSeekerWrapper>
                        );
                      })()}
                    </NotificationTextWrapper>

                    <UnreadDot $visible={isVisuallyUnread} />
                  </NotificationRow>

                  {index < notifications.length - 1 && <NotificationDivider />}
                </>
              );

              return (
                <React.Fragment key={notification.id}>
                  {onDismiss ? (
                    <SwipeableRow onDismiss={() => onDismiss(notification.id)}>
                      {rowContent}
                    </SwipeableRow>
                  ) : (
                    rowContent
                  )}
                </React.Fragment>
              );
            })}
            {isLoading && <LoadMoreSpinner><UniversalSpinner /></LoadMoreSpinner>}
          </NotificationsList>
        ) : (
          <EmptyStateText>No notifications yet.</EmptyStateText>
        )}
      </NotificationCenterContent>
    </>,
    document.body
  );
};

export default NotificationCollection;
